import { AlertTriangle } from "lucide-react";

/**
 * CAO chip + change-of-mind banner for Irish undergraduate courses.
 *
 * Ireland's national admissions run through CAO with fixed dates:
 *   • Round 1 closing:      1 February of the intake year
 *   • Late applications:    1 May
 *   • Change of Mind:       1 July
 *
 * `<CAODeadlineChip degree={...} />` — inline pill for degree cards.
 * `<CAOChangeOfMindBanner degree={...} />` — warning banner shown on
 *    the degree-detail page between mid-June and early July, when
 *    the change-of-mind cutoff is imminent.
 *
 * Both components render nothing for non-Irish / non-CAO degrees, so
 * they're safe to drop into shared card templates unconditionally.
 */

// ── Shared helpers ────────────────────────────────────────────────

const _MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Returns true iff `degree.country === "Ireland"` AND we have a
 *  positive signal that the row is on the CAO platform. */
function _isIrishCAO(degree) {
  if (!degree) return false;
  const country = (degree.country || "").trim().toLowerCase();
  if (country !== "ireland") return false;
  const raw = degree.application_platform || (degree.application && degree.application.platform) || "";
  const platform = String(typeof raw === "object" ? raw.name || "" : raw).toLowerCase();
  // The Ireland CAO backfill enforces platform="CAO" (or a compound
  // like "CAO (EU) / Direct (Non-EU)"), and every Irish undergrad row
  // ships through CAO nationally — so if platform is missing but the
  // country is Ireland, we still treat it as CAO.
  if (!platform) return true;
  return platform.includes("cao");
}

/** Parses an ISO date (YYYY-MM-DD) into { day, monthName, year }. */
function _fmtISODate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [_, y, mm, dd] = m;
  const monthIdx = parseInt(mm, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return { day: parseInt(dd, 10), monthName: _MONTHS_LONG[monthIdx], year: y };
}

// ── CAODeadlineChip ───────────────────────────────────────────────

export function CAODeadlineChip({ degree, testid }) {
  if (!_isIrishCAO(degree)) return null;

  const closes =
    degree?.application_deadline ||
    degree?.application?.closes ||
    null;

  // Fall back to synthesising the Round-1 date from the intake year
  // when the row didn't carry an explicit `application_deadline`.
  // (Shouldn't happen post-backfill, but keeps the chip resilient
  // if a new Irish degree lands before the wave re-runs.)
  const fallback = (() => {
    const intake = degree?.intake || "";
    const m = intake.match(/^(\d{4})/);
    if (!m) return null;
    return `${m[1]}-02-01`;
  })();

  const iso = closes || fallback;
  const parsed = _fmtISODate(iso);
  if (!parsed) return null;

  const label = `CAO · Round 1 closes ${parsed.day} ${parsed.monthName} ${parsed.year}`;

  return (
    <span
      data-testid={testid || "cao-deadline-chip"}
      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 text-[11px] font-medium px-2.5 py-1"
      title="Ireland's Central Applications Office — the national platform for undergraduate admissions"
    >
      <span aria-hidden>🇮🇪</span>
      <span>{label}</span>
    </span>
  );
}

// ── CAOChangeOfMindBanner ─────────────────────────────────────────

/**
 * Warning banner shown on Irish course detail pages between mid-June
 * and early July, when the CAO change-of-mind window is closing.
 *
 * Windows:
 *   • Active window: 15 June → 1 July (inclusive of intake year)
 *   • Shows for both current-year and next-year intakes when the
 *     calendar year matches the intake year.
 *
 * The change-of-mind cutoff is 1 July 17:00 — applicants can revise
 * their CAO course order up until that moment.
 */
export function CAOChangeOfMindBanner({ degree, now = new Date(), testid }) {
  if (!_isIrishCAO(degree)) return null;

  // Anchor to the intake year so banners align with the CAO cycle
  // the family is actually applying to (e.g. a Feb 2027 intake's
  // change-of-mind lands 1 July 2027, not 1 July 2026).
  const intake = degree?.intake || "";
  const im = intake.match(/^(\d{4})/);
  if (!im) return null;
  const intakeYear = parseInt(im[1], 10);

  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;  // 1-12
  const nowDay = now.getDate();

  // Only show when the calendar has caught up with the intake year's
  // CAO cycle AND we're inside the 15 Jun – 1 Jul window.
  if (nowYear !== intakeYear) return null;
  const inWindow =
    (nowMonth === 6 && nowDay >= 15) ||
    (nowMonth === 7 && nowDay === 1);
  if (!inWindow) return null;

  // Days remaining until 1 July (inclusive of same-day).
  const target = new Date(intakeYear, 6, 1);  // 1 July, month index 6
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.max(
    0,
    Math.ceil((target.setHours(23, 59, 59, 999) - now.getTime()) / msPerDay),
  );

  return (
    <div
      data-testid={testid || "cao-change-of-mind-banner"}
      className="mt-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
      role="status"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-700" aria-hidden />
      <div className="text-sm leading-relaxed">
        <div className="font-semibold">
          CAO Change of Mind closes {daysLeft === 0 ? "today" : `in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`} — 1 July {intakeYear}, 17:00
        </div>
        <div className="text-[13px] text-amber-800 mt-0.5">
          You can still revise the order of courses on your CAO application at
          {" "}<a href="https://www.cao.ie" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">cao.ie</a>{" "}
          before the deadline. After 17:00 on 1 July your list is locked for
          Round 1 offers.
        </div>
      </div>
    </div>
  );
}

export default CAODeadlineChip;
